import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Stamp, Save } from 'lucide-react';
import { HankoStamp } from './ui/HankoStamp';
import { Card } from './ui/Card';
import { Button } from './ui/Button';
import { api } from '../lib/api';
import { useToast } from './ui/Toast';

/**
 * Personal hanko (inkan) generator (spec §6.4). Renders a 篆書 / tenshō
 * seal-script glyph from a kanji input — three styles (round / square /
 * gourd) so users can pick what feels right. The output SVG is saved
 * to the user's profile via /auth/me/hanko and used in approval PDFs.
 *
 * The "tenshō" feel is approximated by:
 *   - Noto Serif JP at maximum weight (closest stock font to seal-script)
 *     with vertical stretching to mimic the vertical proportions of 篆書.
 *   - Either a circle, square, or gourd-shaped border drawn in vermilion.
 *   - Slight asymmetric kerning + a faint rotation to suggest hand-cut
 *     intaglio carving rather than printer output.
 */
type Shape = 'round' | 'square' | 'gourd';

function renderSvg(text: string, shape: Shape, size = 96): string {
  const r = size / 2;
  const stroke = '#C8102E';
  const sw = Math.max(2, size * 0.04);
  const chars = text.replace(/\s+/g, '').slice(0, 4);

  // Per-shape safe area where glyphs are guaranteed to stay inside
  // the outline (accounting for stroke width + a small visual margin).
  //   round  — the largest square inscribed in the inner circle is
  //            side = (r - sw) * √2; we use 0.96 * that for breathing.
  //   square — full inside-stroke box minus a small padding.
  //   gourd  — two lobes: top lobe smaller, bottom lobe larger.
  let outline = '';
  // [x, y, w, h] cells for each character position
  const cells: Array<[number, number, number, number]> = [];

  if (shape === 'round') {
    outline = `<circle cx="${r}" cy="${r}" r="${r - sw}" fill="none" stroke="${stroke}" stroke-width="${sw}"/>`;
    const innerR = r - sw - size * 0.04;             // a hair of padding
    const safeSide = Math.SQRT2 * innerR * 0.92;     // inscribed square × 92 %
    const left = r - safeSide / 2;
    const top = r - safeSide / 2;
    const cols = chars.length === 1 ? 1 : 2;
    const rows = chars.length <= 2 ? 1 : 2;
    const cellW = safeSide / cols;
    const cellH = safeSide / rows;
    for (let i = 0; i < chars.length; i++) {
      cells.push([left + (i % cols) * cellW, top + Math.floor(i / cols) * cellH, cellW, cellH]);
    }
  } else if (shape === 'square') {
    outline = `<rect x="${sw}" y="${sw}" width="${size - sw * 2}" height="${size - sw * 2}" fill="none" stroke="${stroke}" stroke-width="${sw}" rx="2"/>`;
    const pad = sw + size * 0.06;
    const inner = size - pad * 2;
    const cols = chars.length === 1 ? 1 : 2;
    const rows = chars.length <= 2 ? 1 : 2;
    const cellW = inner / cols;
    const cellH = inner / rows;
    for (let i = 0; i < chars.length; i++) {
      cells.push([pad + (i % cols) * cellW, pad + Math.floor(i / cols) * cellH, cellW, cellH]);
    }
  } else {
    // Gourd / 瓢箪 — top lobe (smaller) + bottom lobe (larger)
    const topR = r * 0.55;
    const botR = r * 0.65;
    const topCy = size * 0.32;
    const botCy = size * 0.72;
    outline = `
      <circle cx="${r}" cy="${topCy}" r="${topR}" fill="none" stroke="${stroke}" stroke-width="${sw}"/>
      <circle cx="${r}" cy="${botCy}" r="${botR}" fill="none" stroke="${stroke}" stroke-width="${sw}"/>
    `;
    // Lobes get their own inscribed boxes; up to 2 chars per lobe.
    const topSide = Math.SQRT2 * (topR - sw) * 0.92;
    const botSide = Math.SQRT2 * (botR - sw) * 0.92;
    const layout: Array<{ cy: number; side: number }> = [
      { cy: topCy, side: topSide },
      { cy: botCy, side: botSide },
    ];
    let placed = 0;
    for (const lobe of layout) {
      if (placed >= chars.length) break;
      const remaining = chars.length - placed;
      const here = Math.min(2, remaining);
      const cellW = lobe.side / here;
      const cellH = lobe.side;
      const left = r - lobe.side / 2;
      const top = lobe.cy - lobe.side / 2;
      for (let i = 0; i < here; i++) {
        cells.push([left + i * cellW, top, cellW, cellH]);
        placed++;
      }
    }
  }

  // Emit each glyph clipped to its cell. textLength + lengthAdjust
  // forces wide Latin caps (M/N/W with weight=900) to fit horizontally;
  // CJK glyphs are already square so they end up unchanged.
  const glyphs = chars.split('').map((ch, i) => {
    const [cellX, cellY, cellW, cellH] = cells[i];
    const cx = cellX + cellW / 2;
    const cy = cellY + cellH / 2 + cellH * 0.13;     // baseline drop for visual centring
    // Conservative font-size — chars never exceed 64 % of the smaller cell axis.
    const fontSize = Math.min(cellW, cellH) * 0.64;
    // Leave 10 % horizontal margin so rotation (±0.7°) can't push past the cell.
    const textLen = cellW * 0.86;
    const rotate = (Math.random() - 0.5) * 1.4;
    return `<text x="${cx}" y="${cy}" text-anchor="middle" font-family="Noto Serif JP, serif" font-weight="900" font-size="${fontSize.toFixed(2)}" textLength="${textLen.toFixed(2)}" lengthAdjust="spacingAndGlyphs" fill="${stroke}" transform="rotate(${rotate.toFixed(2)} ${cx} ${cy})">${escapeXml(ch)}</text>`;
  }).join('');

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}" width="${size}" height="${size}">${outline}${glyphs}</svg>`;
}

function escapeXml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

export function HankoGenerator({ initialText, onSaved }: { initialText?: string; onSaved?: (svgDataUrl: string) => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [text, setText] = useState(initialText ?? '');
  const [shape, setShape] = useState<Shape>('round');
  const [busy, setBusy] = useState(false);

  const svg = useMemo(() => (text.trim() ? renderSvg(text.trim(), shape) : null), [text, shape]);
  const dataUrl = useMemo(
    () => (svg ? `data:image/svg+xml;utf8,${encodeURIComponent(svg)}` : null),
    [svg],
  );

  async function save() {
    if (!dataUrl) return;
    setBusy(true);
    try {
      await api.patch('/auth/me/hanko', { imageDataUrl: dataUrl, sealText: text.trim() });
      onSaved?.(dataUrl);
    } catch {
      toast.error(t('settings.hankoSaveError'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="p-5">
      <div className="text-[11px] uppercase tracking-[0.16em] text-ink-500 mb-3">{t('settings.hankoGenerator')}</div>
      <div className="grid sm:grid-cols-[1fr_auto] gap-5 items-start">
        <div className="space-y-3">
          <label className="block">
            <div className="text-xs uppercase tracking-wider text-ink-500 mb-1">{t('settings.hankoText')}</div>
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              maxLength={4}
              placeholder="田中"
              className="w-full h-10 px-3 rounded-lg border border-ink-300 dark:border-ink-700 bg-white dark:bg-ink-900 text-base"
            />
            <div className="text-[11px] text-ink-400 mt-1">{t('settings.hankoTextHint')}</div>
          </label>
          <div>
            <div className="text-xs uppercase tracking-wider text-ink-500 mb-1">{t('settings.hankoShape')}</div>
            <div className="grid grid-cols-3 gap-2">
              {(['round', 'square', 'gourd'] as const).map((s) => (
                <button
                  type="button"
                  key={s}
                  onClick={() => setShape(s)}
                  className={`rounded-lg border px-3 py-2 text-sm ${
                    shape === s
                      ? 'border-accent-600 bg-accent-50 dark:bg-accent-900/30'
                      : 'border-ink-200 dark:border-ink-700 hover:border-ink-400'
                  }`}
                >
                  {t(`settings.hankoShapes.${s}`)}
                </button>
              ))}
            </div>
          </div>
          <Button onClick={save} disabled={!svg || busy}>
            <Save size={14} /> {busy ? t('common.loading') : t('settings.hankoSave')}
          </Button>
        </div>

        <div className="flex flex-col items-center gap-2">
          <div className="rounded-2xl bg-pearl-50 dark:bg-ink-900 p-4 border border-ink-200 dark:border-ink-800">
            {svg ? (
              <div dangerouslySetInnerHTML={{ __html: svg }} aria-label={`Hanko preview: ${text}`} />
            ) : (
              <div className="w-24 h-24 flex items-center justify-center text-ink-300">
                <Stamp size={32} />
              </div>
            )}
          </div>
          <div className="text-[11px] text-ink-400">{t('settings.hankoPreview')}</div>
        </div>
      </div>
      <HankoStamp text="" size={0} className="hidden" />
    </Card>
  );
}
